import BikeColor from "./BikeColor";
import BikeColorPicker from "./BikeColorPicker";
import IngresoReview from "./IngresoReview";
import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Grid,
  Divider,
  TextField,
  Button,
  Chip,
  Alert,
  InputAdornment,
  Tooltip,
  Typography,
  alpha,
  Fade,
  IconButton,
  Avatar,
  Paper,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Stepper,
  Step,
  StepButton,
  StepConnector,
  stepConnectorClasses,
  styled,
} from "@mui/material";
import {
  Add,
  Remove,
  Close,
  Person,
  Phone,
  DirectionsBike,
  Numbers,
  PedalBike,
  PhotoCamera,
  Save,
  Print,
  CheckCircle,
  Badge,
  Info,
  QrCode as QrCodeIcon,
  CreditCard,
  Lock,
  CloudUpload,
} from "@mui/icons-material";

import MDBox from "shared/components/MDBox";
import MDTypography from "shared/components/MDTypography";
import MDButton from "shared/components/MDButton";
import DataTable from "shared/components/Tables/DataTable";
import CameraCapture from "./CameraCapture";
import TicketPrinter from "./TicketPrinter";
import { buildBikePrintHTML } from "../utils/buildBikePrintHTML";
import { DOCUMENT_TYPES } from "shared/constants/documentTypes";
import { uploadBikeImagesService } from "modules/came/services/bicicletasService";

import QRCode from "qrcode.react";
import useFormBicicletaModal from "../hooks/useFormBicicletaModal";

const INPUT_HEIGHT = "44px";

const ColorConnector = styled(StepConnector)(({ ownerState }) => ({
  [`&.${stepConnectorClasses.alternativeLabel}`]: {
    top: 14,
    left: "calc(-50% + 20px)",
    right: "calc(50% + 20px)",
  },
  [`& .${stepConnectorClasses.line}`]: {
    borderColor: "#d2dce6",
    borderTopWidth: 2,
    borderRadius: 1,
  },
}));

const ColorStepIconRoot = styled("div")(({ ownerState }) => ({
  width: 28,
  height: 28,
  borderRadius: "50%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: "0.75rem",
  fontWeight: 700,
  transition: "all 0.2s ease",
  backgroundColor: ownerState?.completed ? "#2e7d32" : ownerState?.active ? "#193e54" : "#e2e8f0",
  color: ownerState?.completed || ownerState?.active ? "#ffffff" : "#94a3b8",
  boxShadow: ownerState?.active
    ? `0 0 0 4px ${alpha(ownerState?.accentColor || "#1a237e", 0.15)}`
    : "none",
}));

const ColorStepIcon = (props) => {
  const { active, completed, className, icon, ownerState } = props;
  return (
    <ColorStepIconRoot ownerState={{ ...ownerState, active, completed }} className={className}>
      {completed ? <CheckCircle sx={{ fontSize: 16 }} /> : icon}
    </ColorStepIconRoot>
  );
};

ColorStepIcon.propTypes = {
  active: PropTypes.bool,
  completed: PropTypes.bool,
  className: PropTypes.string,
  icon: PropTypes.node,
  ownerState: PropTypes.object,
};

const UserBikeIdCell = ({ value, row }) => {
  const originalRow = row?.original || row;
  return (
    <MDTypography variant="caption" fontWeight="bold" color="info">
      #{originalRow?.id || value}
    </MDTypography>
  );
};

UserBikeIdCell.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  row: PropTypes.object,
};

UserBikeIdCell.defaultProps = {
  value: "",
  row: {},
};

const UserBikeBrandCell = ({ value, row }) => {
  const originalRow = row?.original || row;
  return (
    <MDBox>
      <MDTypography
        variant="caption"
        display="block"
        fontWeight="600"
        sx={{ color: "#1e293b", fontSize: "0.85rem" }}
      >
        {originalRow?.serie || value || "—"}
      </MDTypography>
      <MDTypography variant="caption" display="block" sx={{ color: "#64748b", fontSize: "0.7rem" }}>
        ID: #{originalRow?.id || "—"}
      </MDTypography>
    </MDBox>
  );
};

UserBikeBrandCell.propTypes = {
  value: PropTypes.string,
  row: PropTypes.shape({
    original: PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      serie: PropTypes.string,
    }),
    serie: PropTypes.string,
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
};

UserBikeBrandCell.defaultProps = {
  value: "",
  row: {},
};

const UserBikeColorCell = ({ value, row }) => {
  const originalRow = row?.original || row;
  const colorValue = originalRow?.color || value;
  return <BikeColor value={colorValue} />;
};

UserBikeColorCell.propTypes = {
  value: PropTypes.string,
  row: PropTypes.object,
};

UserBikeColorCell.defaultProps = {
  value: "",
  row: {},
};

// ✅ FIX PRINCIPAL: leer row.original.rawBike
const UserBikeActionCell = ({ row, selectedUserBikeId, onSelect }) => {
  // El DataTable envuelve los datos en row.original
  const originalRow = row?.original || row;
  const isSelected = selectedUserBikeId === originalRow?.id;

  const handleClick = (e) => {
    e.preventDefault();
    e.stopPropagation();

    const rawBike = originalRow?.rawBike;

    if (!rawBike) {
      console.error("❌ rawBike no existe en originalRow");
      return;
    }
    if (typeof onSelect !== "function") {
      console.error("❌ onSelect no es una función");
      return;
    }

    onSelect(rawBike);
  };

  return (
    <MDButton
      size="small"
      variant={isSelected ? "contained" : "outlined"}
      onClick={handleClick}
      type="button"
      sx={{
        fontSize: "0.7rem",
        py: 0.5,
        px: 2,
        minWidth: 90,
        textTransform: "none",
        bgcolor: isSelected ? "#4caf50" : "transparent",
        color: isSelected ? "#fff !important" : "#1976d2",
        borderColor: "#1976d2",
        "&:hover": {
          bgcolor: isSelected ? "#388e3c" : alpha("#1976d2", 0.08),
        },
      }}
    >
      {isSelected ? "✓ Seleccionada" : "Usar"}
    </MDButton>
  );
};

UserBikeActionCell.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      rawBike: PropTypes.object,
    }),
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    rawBike: PropTypes.object,
  }),
  selectedUserBikeId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onSelect: PropTypes.func,
};

UserBikeActionCell.defaultProps = {
  row: {},
  selectedUserBikeId: null,
  onSelect: null,
};

const BicicletaModal = ({
  open,
  onClose,
  modo,
  ganchos,
  ganchoSeleccionado,
  onGanchoSelect,
  formData: initialFormData,
  onFormDataChange,
  onSaveUser,
  onSaveBike,
  onSubmit,
  users = [],
  loading = false,
}) => {
  const safeInitialData = initialFormData || {};
  const safeUsers = Array.isArray(users) ? users : [];

  const form = useFormBicicletaModal(
    safeInitialData,
    {
      onSaveUser,
      onSaveBike,
      onSubmit,
    },
    safeUsers
  );

  const formData = form?.formData || {};

  const [fotos, setFotos] = useState({
    cedula: { tomada: false, preview: null },
    bicicleta: { tomada: false, preview: null },
    usuario: { tomada: false, preview: null },
  });
  const [cameraOpen, setCameraOpen] = useState(false);
  const [cameraTipo, setCameraTipo] = useState(null);
  const [selectedGancho, setSelectedGancho] = useState(ganchoSeleccionado || null);
  const [activeStep, setActiveStep] = useState(0);
  const [creatingBike, setCreatingBike] = useState(false);
  const submitLock = useRef(false);
  const contentRef = useRef(null);
  const [registroCompletado, setRegistroCompletado] = useState(false);
  const [numeroRegistro, setNumeroRegistro] = useState(null);
  const [showTicket, setShowTicket] = useState(false);
  const [openPreviewTicket, setOpenPreviewTicket] = useState(false);

  const [uploadingImages, setUploadingImages] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [uploadSuccess, setUploadSuccess] = useState(false);

  const [selectedUserBikeId, setSelectedUserBikeId] = useState(null);

  useEffect(() => {
    if (!ganchoSeleccionado) {
      setSelectedGancho(null);
      return;
    }
    setSelectedGancho(ganchoSeleccionado);
    if (ganchoSeleccionado?.id && form?.handleChange) {
      form.handleChange("ganchoId", ganchoSeleccionado.id);
    }
  }, [ganchoSeleccionado]);

  useEffect(() => {
    if (onFormDataChange && form) {
      onFormDataChange("savedUser", form.savedUser);
    }
  }, [form?.savedUser]);

  useEffect(() => {
    if (onFormDataChange && form) {
      onFormDataChange("savedBike", form.savedBike);
    }
  }, [form?.savedBike]);

  const handleChange = (field, value) => {
    if (form?.handleChange) form.handleChange(field, value);
    if (onFormDataChange) onFormDataChange(field, value);
  };

  const handleSaveUser = async () => {
    if (!form?.saveUser) return;
    const result = await form.saveUser();
    if (result?.success) {
      onFormDataChange?.("savedUser", result.data);
      setActiveStep(1);
    }
    return result;
  };

  const handleSaveBike = async () => {
    if (!form?.saveBike) return;
    const result = await form.saveBike();
    if (result?.success) {
      onFormDataChange?.("savedBike", result.data);
      setActiveStep(2);
    }
    return result;
  };

  const handleSelectUserBike = useCallback(
    (bike) => {
      if (!bike) {
        console.error("❌ bike es null/undefined");
        return;
      }

      setSelectedUserBikeId(bike.id);

      if (form?.selectExistingBike) {
        form.selectExistingBike(bike);
        setActiveStep(2);
      } else {
        console.error("❌ form.selectExistingBike no está disponible");
      }
    },
    [form]
  );

  const buildPrintHTML = () => {
    const qrCanvas = document.getElementById("qr-print-canvas");
    const qrImg = document.getElementById("qr-print-img");
    const qrImageSrc =
      qrCanvas?.tagName === "CANVAS" ? qrCanvas.toDataURL("image/png") : qrImg?.src || "";
    return buildBikePrintHTML({
      formData,
      modo,
      fotos,
      ganchoId: selectedGancho?.id,
      numeroRegistro,
      qrImageSrc,
    });
  };

  const autoPrintTicket = () => {
    const html = buildPrintHTML();

    let iframe = document.getElementById("print-iframe");
    if (!iframe) {
      iframe = document.createElement("iframe");
      iframe.id = "print-iframe";
      iframe.style.position = "absolute";
      iframe.style.width = "0";
      iframe.style.height = "0";
      iframe.style.border = "none";
      iframe.style.left = "-9999px";
      document.body.appendChild(iframe);
    }

    const doc = iframe.contentWindow.document;
    doc.open();
    doc.write(html);
    doc.close();

    setTimeout(() => {
      try {
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
      } catch (err) {
        console.error("❌ Error al imprimir:", err);
      }
    }, 600);
  };

  const confirmAction = async () => {
    if (!form?.submitForm) return;

    const result = await form.submitForm({
      ganchoId: selectedGancho?.id,
      modo,
      fotos,
    });

    if (result?.success) {
      const cicloId =
        result.data?.id || result.data?.id_cycle || result.data?.insertId || result.data?.insert_id;

      const numero = String(cicloId || Date.now()).slice(-6);
      setNumeroRegistro(numero);
      setRegistroCompletado(true);
      setShowTicket(true);

      const hasFotos = fotos.cedula.tomada || fotos.bicicleta.tomada || fotos.usuario.tomada;

      if (cicloId && hasFotos) {
        setUploadingImages(true);
        setUploadError(null);
        setUploadSuccess(false);

        try {
          const uploadResult = await uploadBikeImagesService(cicloId, {
            cedula: fotos.cedula.preview,
            bicicleta: fotos.bicicleta.preview,
            usuario: fotos.usuario.preview,
          });

          if (uploadResult.success) {
            setUploadSuccess(true);
          } else {
            setUploadError(uploadResult.error || "No se pudieron subir las fotos");
          }
        } catch (err) {
          console.error("❌ Error al subir fotos:", err);
          setUploadError(err.message || "Error al subir fotos");
        } finally {
          setUploadingImages(false);
        }
      }

      setTimeout(() => {
        autoPrintTicket();
      }, 1200);
    } else if (contentRef.current) {
      // submitForm exposes the message in the inline Alert above the review.
      contentRef.current.scrollTop = 0;
    }
  };

  const handleImprimirTicket = () => {
    if (registroCompletado) {
      setShowTicket(true);
      setTimeout(() => autoPrintTicket(), 600);
    }
  };

  const openPreview = () => {
    setOpenPreviewTicket(true);
  };

  const openCamera = (tipo) => {
    setCameraTipo(tipo);
    setCameraOpen(true);
  };

  const closeCamera = () => {
    setCameraOpen(false);
    setCameraTipo(null);
  };

  const handlePhotoCapture = (imageData) => {
    if (cameraTipo) {
      setFotos((prev) => ({
        ...prev,
        [cameraTipo]: { tomada: true, preview: imageData },
      }));
    }
    closeCamera();
  };

  const totalFotosTomadas = Object.values(fotos).filter((f) => f.tomada).length;
  const totalFotos = Object.keys(fotos).length;

  const getFotoLabel = (tipo) => {
    switch (tipo) {
      case "cedula":
        return "Cédula";
      case "bicicleta":
        return "Bicicleta";
      case "usuario":
        return "Usuario";
      default:
        return "";
    }
  };

  const getFotoIcon = (tipo) => {
    switch (tipo) {
      case "cedula":
        return <CreditCard />;
      case "bicicleta":
        return <DirectionsBike />;
      case "usuario":
        return <Person />;
      default:
        return <PhotoCamera />;
    }
  };

  const qrData = {
    id: numeroRegistro || Date.now().toString().slice(-6),
    nombre: formData.nombre || "",
    cedula: formData.cedula || "",
    color: formData.color || "",
    serie: formData.serie || "",
    gancho: selectedGancho?.id || "",
    modo: modo || "",
    fecha: new Date().toISOString(),
  };
  const qrValue = JSON.stringify(qrData);

  const userBikes = form?.userBikes || [];
  const isNewBikeMode = creatingBike || userBikes.length === 0;
  const canSaveUser = form?.isUserFormValid?.() && !form?.savingUser && !form?.savedUser;
  const canSaveBike =
    !!form?.savedUser && form?.isBikeFormValid?.() && !form?.savingBike && !form?.savedBike;
  const canSubmit =
    !!form?.savedUser && !!form?.savedBike && !!selectedGancho?.id && !form?.isSubmitting;

  const reachableStep = !form?.savedUser ? 0 : !form?.savedBike ? 1 : !selectedGancho?.id ? 2 : 3;

  const isIngreso = modo === "INGRESO";
  const accentColor = isIngreso ? "#245a83" : "#c62828";

  const inputSx = {
    "& .MuiInputBase-root": { height: INPUT_HEIGHT, fontSize: "1rem" },
    "& .MuiInputLabel-root": { fontSize: "0.9rem" },
    "& .MuiInputLabel-shrink": { fontSize: "0.85rem" },
    "& .MuiInputAdornment-root svg": { fontSize: "22px" },
  };

  const selectSx = {
    height: INPUT_HEIGHT,
    fontSize: "1rem",
    "& .MuiSelect-select": { display: "flex", alignItems: "center" },
  };

  const steps = ["Usuario", "Bicicleta", "Gancho", "Confirmar"];
  const busy = Boolean(
    loading || form?.savingUser || form?.savingBike || form?.isSubmitting || uploadingImages
  );
  const showUserDetails = Boolean(
    form?.savedUser ||
      form?.foundUser ||
      (form?.userLookupDone && !form?.searchingUser) ||
      (!form?.loadingUsers &&
        form?.usersCache?.length === 0 &&
        (formData.cedula || "").trim().length >= 6) ||
      formData.nombre
  );
  useEffect(() => {
    if (activeStep > reachableStep && !registroCompletado) setActiveStep(reachableStep);
  }, [activeStep, reachableStep, registroCompletado]);
  useEffect(() => {
    if (contentRef.current) contentRef.current.scrollTop = 0;
  }, [activeStep]);
  const continueStep = async () => {
    if (busy || registroCompletado) return;
    if (activeStep === 0) {
      if (form?.savedUser) setActiveStep(1);
      else await handleSaveUser();
    } else if (activeStep === 1) {
      if (form?.savedBike) setActiveStep(2);
      else if (isNewBikeMode) await handleSaveBike();
    } else if (activeStep === 2 && selectedGancho?.id) setActiveStep(3);
    else if (activeStep === 3 && canSubmit && !submitLock.current) {
      submitLock.current = true;
      try {
        await confirmAction();
      } finally {
        submitLock.current = false;
      }
    }
  };
  const nextDisabled =
    busy ||
    registroCompletado ||
    (activeStep === 0
      ? !form?.savedUser && !canSaveUser
      : activeStep === 1
      ? !form?.savedBike && (!isNewBikeMode || !canSaveBike)
      : activeStep === 2
      ? !selectedGancho?.id
      : !canSubmit);
  const nextLabel = busy
    ? "Procesando…"
    : activeStep === 0
    ? form?.savedUser
      ? "Continuar"
      : form?.foundUser
      ? "Usar usuario y continuar"
      : "Guardar usuario y continuar"
    : activeStep === 1
    ? form?.savedBike
      ? "Continuar"
      : "Guardar bicicleta y continuar"
    : activeStep === 2
    ? "Continuar"
    : isIngreso
    ? "Registrar ingreso"
    : "Registrar salida";

  const ticketData = {
    ...formData,
    nombreCompleto: form?.getNombreCompleto?.() || "",
    gancho: selectedGancho?.id || "",
    modo: modo,
    fotos: {
      cedula: fotos.cedula.tomada,
      bicicleta: fotos.bicicleta.tomada,
      usuario: fotos.usuario.tomada,
    },
  };

  const userBikesRows = useMemo(
    () =>
      userBikes.map((bike) => ({
        _rowKey: `bike-${bike.id}-${selectedUserBikeId === bike.id ? "sel" : "no"}`,
        id: bike.id,
        marca: bike.brand || bike.marca || bike.code || "—",
        serie: bike.code || bike.serie || "—",
        color: bike.color || "—",
        rawBike: bike,
      })),
    [userBikes, selectedUserBikeId]
  );

  const userBikesColumns = [
    { Header: "#", accessor: "id", width: "8%", Cell: UserBikeIdCell },
    {
      Header: "Código / Serie",
      accessor: "marca",
      width: "35%",
      Cell: UserBikeBrandCell,
    },
    { Header: "Color", accessor: "color", width: "20%", Cell: UserBikeColorCell },
    {
      Header: "Acción",
      accessor: "action",
      width: "37%",
      Cell: (props) => (
        <UserBikeActionCell
          {...props}
          selectedUserBikeId={selectedUserBikeId}
          onSelect={handleSelectUserBike}
        />
      ),
    },
  ];

  return (
    <>
      {cameraOpen && (
        <CameraCapture onCapture={handlePhotoCapture} onClose={closeCamera} tipo={cameraTipo} />
      )}

      <div id="ticket-hidden-container" style={{ position: "absolute", left: "-9999px", top: 0 }}>
        <div id="ticket-visible">
          <TicketPrinter
            data={ticketData}
            qrValue={qrValue}
            numeroRegistro={numeroRegistro || "000000"}
            onClose={() => {}}
            hideActions
          />
        </div>
      </div>

      <Dialog
        open={showTicket}
        onClose={() => setShowTicket(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2, overflow: "hidden" } }}
      >
        <TicketPrinter
          data={ticketData}
          qrValue={qrValue}
          numeroRegistro={numeroRegistro || "000000"}
          onClose={() => setShowTicket(false)}
        />
      </Dialog>

      <Dialog
        open={openPreviewTicket}
        onClose={() => setOpenPreviewTicket(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2, overflow: "hidden" } }}
      >
        <DialogTitle
          sx={{
            bgcolor: alpha("#1a237e", 0.06),
            py: 2,
            px: 3,
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center" gap={1.5}>
              <Box
                sx={{
                  bgcolor: alpha("#1a237e", 0.12),
                  borderRadius: "8px",
                  p: 0.75,
                  display: "flex",
                }}
              >
                <Print sx={{ color: "#1a237e", fontSize: 20 }} />
              </Box>
              <Box>
                <Typography variant="h6" fontWeight="bold">
                  Vista Previa del Ticket
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Así se verá impreso (formato 80mm)
                </Typography>
              </Box>
            </Box>
            <IconButton
              onClick={() => setOpenPreviewTicket(false)}
              size="small"
              sx={{ color: "#64748b" }}
            >
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent
          sx={{
            p: 3,
            bgcolor: "#e2e8f0",
            maxHeight: "75vh",
            overflowY: "auto",
          }}
        >
          <Box
            sx={{
              maxWidth: "380px",
              mx: "auto",
              boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
              borderRadius: 2,
              overflow: "hidden",
              bgcolor: "#fff",
              border: "1px dashed #cbd5e1",
            }}
          >
            <Box
              sx={{
                bgcolor: alpha("#64748b", 0.08),
                px: 2,
                py: 0.5,
                textAlign: "center",
                borderBottom: "1px dashed #cbd5e1",
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  fontSize: "0.6rem",
                  color: "#64748b",
                  letterSpacing: 1,
                  fontWeight: 600,
                }}
              >
                ▼ 80mm ▼
              </Typography>
            </Box>

            <TicketPrinter
              data={ticketData}
              qrValue={qrValue}
              numeroRegistro={numeroRegistro || "000000"}
              onClose={() => setOpenPreviewTicket(false)}
              hideActions
            />

            <Box
              sx={{
                bgcolor: alpha("#64748b", 0.08),
                px: 2,
                py: 0.5,
                textAlign: "center",
                borderTop: "1px dashed #cbd5e1",
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  fontSize: "0.6rem",
                  color: "#64748b",
                  letterSpacing: 1,
                  fontWeight: 600,
                }}
              >
                ▲ 80mm ▲
              </Typography>
            </Box>
          </Box>

          <Alert
            severity="info"
            icon={<Info sx={{ fontSize: 18 }} />}
            sx={{ mt: 2, maxWidth: 380, mx: "auto", borderRadius: "8px", fontSize: "0.75rem" }}
          >
            Esta es una simulación. El ticket real se imprimirá en papel térmico de 80mm.
          </Alert>
        </DialogContent>

        <DialogActions sx={{ p: 2.5, gap: 2, bgcolor: "#f8fafc" }}>
          <MDButton
            onClick={() => setOpenPreviewTicket(false)}
            variant="outlined"
            startIcon={<Close />}
            sx={{ px: 4, py: 1.2, borderRadius: "10px", fontWeight: 600 }}
          >
            Cerrar
          </MDButton>
          <MDButton
            onClick={() => {
              autoPrintTicket();
            }}
            variant="contained"
            startIcon={<Print />}
            sx={{
              px: 4,
              py: 1.2,
              borderRadius: "10px",
              fontWeight: 700,
              bgcolor: "#1a237e",
              color: "#ffffff !important",
              "&:hover": { bgcolor: "#0d1445" },
            }}
          >
            Imprimir Ahora
          </MDButton>
        </DialogActions>
      </Dialog>

      <Dialog
        open={open}
        onClose={busy ? undefined : onClose}
        TransitionComponent={Fade}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: "16px", maxHeight: "92vh", width: "100%", overflow: "hidden" },
        }}
      >
        <DialogTitle
          sx={{
            pb: 2,
            pt: 2.5,
            px: 3,
            borderBottom: 1,
            borderColor: "divider",
            bgcolor: "background.paper",
          }}
        >
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2.5}>
            <Box display="flex" alignItems="center" gap={1.5}>
              <Box
                sx={{
                  bgcolor: alpha(accentColor, 0.1),
                  borderRadius: "10px",
                  p: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {isIngreso ? (
                  <Add sx={{ fontSize: "1.5rem", color: accentColor }} />
                ) : (
                  <Remove sx={{ fontSize: "1.5rem", color: accentColor }} />
                )}
              </Box>
              <Box>
                <Typography variant="h6" fontWeight="bold">
                  {isIngreso ? "Registrar Ingreso" : "Registrar Salida"}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Complete los pasos para registrar el movimiento
                </Typography>
              </Box>
            </Box>
            <IconButton
              aria-label="Cerrar registro"
              disabled={busy}
              onClick={onClose}
              size="small"
              sx={{ color: "#64748b" }}
            >
              <Close />
            </IconButton>
          </Box>

          <Box sx={{ pb: 0.5 }}>
            <Stepper
              activeStep={activeStep}
              nonLinear
              sx={{
                background: "#f3f6fa",
                backgroundImage: "none",
                boxShadow: "none",
                border: "1px solid #dce5ed",
                borderRadius: 2,
                p: 1.5,
              }}
              alternativeLabel
              connector={<ColorConnector ownerState={{ accentColor }} />}
            >
              {steps.map((label, index) => (
                <Step
                  key={label}
                  completed={
                    registroCompletado ||
                    (index === 0
                      ? !!form?.savedUser
                      : index === 1
                      ? !!form?.savedBike
                      : index === 2
                      ? !!selectedGancho?.id
                      : false)
                  }
                >
                  <StepButton
                    onClick={() => setActiveStep(index)}
                    disabled={busy || registroCompletado || index > reachableStep}
                    icon={
                      <ColorStepIcon
                        icon={index + 1}
                        active={activeStep === index}
                        completed={registroCompletado || index < reachableStep}
                        ownerState={{ accentColor: "#193e54" }}
                      />
                    }
                    sx={{
                      textTransform: "none",
                      color: "#243b53",
                      "& .MuiStepLabel-label": {
                        fontSize: "0.8rem",
                        fontWeight: 700,
                        mt: 0.5,
                        color: "#243b53 !important",
                        opacity: 1,
                      },
                      "& .MuiStepLabel-label.Mui-active": {
                        color: "#243b53 !important",
                        fontWeight: 800,
                        opacity: 1,
                      },
                      "& .MuiStepLabel-label.Mui-completed": {
                        color: "#243b53 !important",
                        fontWeight: 700,
                        opacity: 1,
                      },
                      "& .MuiStepLabel-label.Mui-disabled": {
                        color: "#243b53 !important",
                        opacity: 1,
                      },
                      "& .MuiStepLabel-label.MuiStepLabel-alternativeLabel": {
                        opacity: 1,
                      },
                    }}
                  >
                    {label}
                  </StepButton>
                </Step>
              ))}
            </Stepper>
          </Box>
        </DialogTitle>

        <DialogContent
          ref={contentRef}
          dividers
          sx={{
            p: { xs: 2, sm: 3 },
            overflowY: "auto",
            bgcolor: "#f8fafc",
            minHeight: "min(300px, 45vh)",
          }}
        >
          {registroCompletado && (
            <Alert severity="success" sx={{ mb: 2 }}>
              Movimiento registrado. Puedes imprimir el ticket o cerrar.
            </Alert>
          )}
          {form?.submitError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {form.submitError}
            </Alert>
          )}
          <Stack spacing={2.5}>
            {activeStep === 0 && (
              <>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2.5,
                    borderRadius: "12px",
                    border: "1px solid",
                    borderColor: form?.savedUser
                      ? alpha("#4caf50", 0.5)
                      : form?.foundUser
                      ? alpha("#1976d2", 0.5)
                      : "divider",
                    bgcolor: "#fff",
                    position: "relative",
                    overflow: "hidden",
                  }}
                >
                  <Box
                    sx={{
                      position: "absolute",
                      left: 0,
                      top: 0,
                      bottom: 0,
                      width: 4,
                      bgcolor: form?.savedUser
                        ? "#4caf50"
                        : form?.foundUser
                        ? "#1976d2"
                        : "#cbd5e1",
                    }}
                  />

                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    mb={2}
                    flexWrap="wrap"
                    gap={1}
                  >
                    <Box display="flex" alignItems="center" gap={1}>
                      <Box
                        sx={{
                          bgcolor: alpha("#5c6bc0", 0.1),
                          borderRadius: "8px",
                          p: 0.75,
                          display: "flex",
                        }}
                      >
                        <Person sx={{ fontSize: 18, color: "#5c6bc0" }} />
                      </Box>
                      <Typography variant="subtitle2" fontWeight="700">
                        Datos del Usuario
                      </Typography>
                      {form?.savedUser && (
                        <Chip
                          size="small"
                          icon={<CheckCircle />}
                          label="Guardado"
                          sx={{
                            height: 20,
                            fontSize: "0.65rem",
                            bgcolor: "#e8f5e9",
                            color: "#2e7d32",
                            "& .MuiChip-icon": { color: "#2e7d32", fontSize: 12 },
                          }}
                        />
                      )}
                      {!form?.savedUser && form?.foundUser && (
                        <Chip
                          size="small"
                          icon={<CheckCircle />}
                          label="Encontrado"
                          sx={{
                            height: 20,
                            fontSize: "0.65rem",
                            bgcolor: "#e3f2fd",
                            color: "#1565c0",
                            "& .MuiChip-icon": { color: "#1565c0", fontSize: 12 },
                          }}
                        />
                      )}
                    </Box>
                  </Box>

                  {form?.searchingUser && (
                    <Alert
                      severity="info"
                      icon={<CircularProgress size={16} />}
                      sx={{ mb: 1.5, borderRadius: "8px", fontSize: "0.75rem", py: 0.5 }}
                    >
                      Buscando usuario con cédula {formData.cedula}...
                    </Alert>
                  )}

                  {!form?.searchingUser && form?.foundUser && (
                    <Alert
                      severity="success"
                      icon={<CheckCircle />}
                      sx={{ mb: 1.5, borderRadius: "8px", fontSize: "0.75rem", py: 0.5 }}
                    >
                      <strong>Usuario encontrado:</strong> {form.foundUser.names}{" "}
                      {form.foundUser.lastnames} — Los datos se autocompletaron.
                    </Alert>
                  )}

                  {!form?.searchingUser &&
                    form?.userLookupDone &&
                    !form?.foundUser &&
                    (formData.cedula || "").length >= 6 && (
                      <Alert
                        severity="warning"
                        icon={<Info />}
                        sx={{ mb: 1.5, borderRadius: "8px", fontSize: "0.75rem", py: 0.5 }}
                      >
                        No se encontró. Complete los datos y guarde.
                      </Alert>
                    )}

                  {form?.userError && (
                    <Alert
                      severity="error"
                      sx={{ mb: 1.5, borderRadius: "8px", fontSize: "0.75rem" }}
                    >
                      {form.userError}
                    </Alert>
                  )}

                  {!showUserDetails && (
                    <Typography variant="body2" sx={{ color: "#526579", mb: 2 }}>
                      Ingresa el documento para buscar al usuario. Si no existe, podrás completar
                      sus datos.
                    </Typography>
                  )}
                  <Grid container spacing={2} sx={{ mt: 0.5 }}>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth size="small" disabled={!!form?.savedUser}>
                        <InputLabel id="id-type-label">Tipo de documento *</InputLabel>
                        <Select
                          labelId="id-type-label"
                          value={formData.idType || 1}
                          label="Tipo de documento *"
                          onChange={(e) => handleChange("idType", e.target.value)}
                          sx={selectSx}
                        >
                          {DOCUMENT_TYPES.map((doc) => (
                            <MenuItem key={doc.id} value={doc.id}>
                              <strong>{doc.code}</strong> — {doc.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Cédula *"
                        value={formData.cedula || ""}
                        onChange={(e) => handleChange("cedula", e.target.value)}
                        onBlur={() => form?.handleBlur?.("cedula")}
                        error={form?.isFieldError?.("cedula")}
                        helperText={form?.isFieldError?.("cedula") && form?.errors?.cedula}
                        variant="outlined"
                        disabled={!!form?.savedUser}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Badge sx={{ color: "#5c6bc0" }} />
                            </InputAdornment>
                          ),
                          endAdornment: form?.searchingUser ? (
                            <InputAdornment position="end">
                              <CircularProgress size={16} />
                            </InputAdornment>
                          ) : form?.foundUser ? (
                            <InputAdornment position="end">
                              <CheckCircle sx={{ color: "#2e7d32" }} />
                            </InputAdornment>
                          ) : null,
                        }}
                        sx={inputSx}
                      />
                    </Grid>

                    {showUserDetails && (
                      <>
                        <Grid item xs={12} sm={6} md={4}>
                          <TextField
                            fullWidth
                            size="small"
                            label="Nombre *"
                            value={formData.nombre || ""}
                            onChange={(e) => handleChange("nombre", e.target.value)}
                            onBlur={() => form?.handleBlur?.("nombre")}
                            error={form?.isFieldError?.("nombre")}
                            helperText={form?.isFieldError?.("nombre") && form?.errors?.nombre}
                            variant="outlined"
                            disabled={!!form?.savedUser}
                            sx={inputSx}
                          />
                        </Grid>

                        <Grid item xs={12} sm={6} md={4}>
                          <TextField
                            fullWidth
                            size="small"
                            label="Apellido *"
                            value={formData.apellido || ""}
                            onChange={(e) => handleChange("apellido", e.target.value)}
                            onBlur={() => form?.handleBlur?.("apellido")}
                            error={form?.isFieldError?.("apellido")}
                            helperText={form?.isFieldError?.("apellido") && form?.errors?.apellido}
                            variant="outlined"
                            disabled={!!form?.savedUser}
                            sx={inputSx}
                          />
                        </Grid>

                        <Grid item xs={12} sm={6} md={4}>
                          <TextField
                            fullWidth
                            size="small"
                            label="Celular *"
                            value={formData.celular || ""}
                            onChange={(e) => handleChange("celular", e.target.value)}
                            onBlur={() => form?.handleBlur?.("celular")}
                            error={form?.isFieldError?.("celular")}
                            helperText={form?.isFieldError?.("celular") && form?.errors?.celular}
                            variant="outlined"
                            disabled={!!form?.savedUser}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <Phone sx={{ color: "#5c6bc0" }} />
                                </InputAdornment>
                              ),
                            }}
                            sx={inputSx}
                          />
                        </Grid>
                      </>
                    )}
                  </Grid>

                  {form?.savedUser && (
                    <Box mt={1.5} display="flex" alignItems="center" gap={1}>
                      <Chip
                        size="small"
                        icon={<Lock />}
                        label="Bloqueado"
                        sx={{
                          bgcolor: "#fff8e1",
                          color: "#e65100",
                          fontWeight: 500,
                          fontSize: "0.7rem",
                          height: 22,
                        }}
                      />
                      <Button
                        size="small"
                        onClick={() => form?.setSavedUser?.(null)}
                        sx={{ textTransform: "none", fontSize: "0.75rem", py: 0 }}
                      >
                        Cambiar
                      </Button>
                    </Box>
                  )}
                </Paper>
              </>
            )}
            {activeStep === 1 && (
              <>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2.5,
                    borderRadius: "12px",
                    border: "1px solid",
                    borderColor: form?.savedBike ? alpha("#4caf50", 0.5) : "divider",
                    bgcolor: "#fff",
                    opacity: form?.savedUser ? 1 : 0.55,
                    position: "relative",
                    overflow: "hidden",
                    transition: "opacity 0.2s",
                  }}
                >
                  <Box
                    sx={{
                      position: "absolute",
                      left: 0,
                      top: 0,
                      bottom: 0,
                      width: 4,
                      bgcolor: form?.savedBike ? "#4caf50" : "#cbd5e1",
                    }}
                  />

                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    mb={2}
                    flexWrap="wrap"
                    gap={1}
                  >
                    <Box display="flex" alignItems="center" gap={1}>
                      <Box
                        sx={{
                          bgcolor: alpha("#5c6bc0", 0.1),
                          borderRadius: "8px",
                          p: 0.75,
                          display: "flex",
                        }}
                      >
                        <DirectionsBike sx={{ fontSize: 18, color: "#5c6bc0" }} />
                      </Box>
                      <Typography variant="subtitle2" fontWeight="700">
                        Datos de la Bicicleta
                      </Typography>
                      {form?.savedBike && (
                        <Chip
                          size="small"
                          icon={<CheckCircle />}
                          label="Guardada"
                          sx={{
                            height: 20,
                            fontSize: "0.65rem",
                            bgcolor: "#e8f5e9",
                            color: "#2e7d32",
                            "& .MuiChip-icon": { color: "#2e7d32", fontSize: 12 },
                          }}
                        />
                      )}
                    </Box>
                  </Box>

                  {!form?.savedUser && (
                    <Alert
                      severity="info"
                      icon={<Info />}
                      sx={{ mb: 1.5, borderRadius: "8px", fontSize: "0.75rem", py: 0.5 }}
                    >
                      Guarde el usuario primero.
                    </Alert>
                  )}

                  {userBikes.length > 0 && !form?.savedBike && (
                    <Box sx={{ mb: 2 }}>
                      <Alert
                        severity="info"
                        icon={<Info sx={{ fontSize: 18 }} />}
                        sx={{ mb: 1.5, borderRadius: "8px", fontSize: "0.75rem", py: 0.5 }}
                      >
                        Este usuario tiene <strong>{userBikes.length}</strong>{" "}
                        {userBikes.length === 1 ? "bicicleta registrada" : "bicicletas registradas"}
                        . Selecciona una para autocompletar los datos o crea una nueva.
                      </Alert>

                      <Box
                        sx={{
                          border: "1px solid",
                          borderColor: "divider",
                          borderRadius: 1.5,
                          overflow: "hidden",
                        }}
                      >
                        <DataTable
                          key={`user-bikes-${selectedUserBikeId || "none"}`}
                          table={{ columns: userBikesColumns, rows: userBikesRows }}
                          isSorted={false}
                          entriesPerPage={false}
                          showTotalEntries={false}
                          noEndBorder
                          canSearch={false}
                        />
                      </Box>
                    </Box>
                  )}

                  {form?.bikeError && (
                    <Alert
                      severity="error"
                      sx={{ mb: 1.5, borderRadius: "8px", fontSize: "0.75rem" }}
                    >
                      {form.bikeError}
                    </Alert>
                  )}

                  {userBikes.length > 0 && !form?.savedBike && (
                    <Button
                      onClick={() => setCreatingBike(!creatingBike)}
                      sx={{ color: "#245a83", mb: 2, textTransform: "none" }}
                    >
                      {creatingBike
                        ? "Elegir una bicicleta registrada"
                        : "Registrar otra bicicleta"}
                    </Button>
                  )}
                  {isNewBikeMode || form?.savedBike ? (
                    <>
                      {" "}
                      <Grid container spacing={2} sx={{ mt: 0.5 }}>
                        <Grid item xs={12} sm={6}>
                          <BikeColorPicker
                            value={formData.color || ""}
                            onChange={(value) => handleChange("color", value)}
                            onBlur={() => form?.handleBlur?.("color")}
                            error={form?.isFieldError?.("color")}
                            helperText={form?.isFieldError?.("color") && form?.errors?.color}
                            disabled={!form?.savedUser || !!form?.savedBike}
                            sx={inputSx}
                          />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            size="small"
                            label="Número de Serie *"
                            value={formData.serie || ""}
                            onChange={(e) => handleChange("serie", e.target.value)}
                            onBlur={() => form?.handleBlur?.("serie")}
                            error={form?.isFieldError?.("serie")}
                            helperText={form?.isFieldError?.("serie") && form?.errors?.serie}
                            variant="outlined"
                            disabled={!form?.savedUser || !!form?.savedBike}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <Numbers sx={{ color: "#5c6bc0" }} />
                                </InputAdornment>
                              ),
                            }}
                            placeholder="Ej: BK-001..."
                            sx={inputSx}
                          />
                        </Grid>
                      </Grid>
                    </>
                  ) : null}

                  {form?.savedBike && (
                    <Box mt={1.5}>
                      <Button
                        size="small"
                        onClick={() => {
                          form?.setSavedBike?.(null);
                          setCreatingBike(true);
                          setSelectedUserBikeId(null);
                        }}
                        sx={{ textTransform: "none", fontSize: "0.75rem" }}
                      >
                        Editar bicicleta
                      </Button>
                    </Box>
                  )}
                </Paper>
              </>
            )}
            {activeStep === 2 && (
              <>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2.5,
                    borderRadius: "12px",
                    border: "1px solid",
                    borderColor: "divider",
                    bgcolor: "#fff",
                    opacity: form?.savedBike ? 1 : 0.55,
                    position: "relative",
                    overflow: "hidden",
                    transition: "opacity 0.2s",
                  }}
                >
                  <Box
                    sx={{
                      position: "absolute",
                      left: 0,
                      top: 0,
                      bottom: 0,
                      width: 4,
                      bgcolor: selectedGancho?.id ? "#4caf50" : "#cbd5e1",
                    }}
                  />

                  <Box display="flex" alignItems="center" gap={1} mb={1.5} flexWrap="wrap">
                    <Box
                      sx={{
                        bgcolor: alpha("#5c6bc0", 0.1),
                        borderRadius: "8px",
                        p: 0.75,
                        display: "flex",
                      }}
                    >
                      <PedalBike sx={{ fontSize: 18, color: "#5c6bc0" }} />
                    </Box>
                    <Typography variant="subtitle2" fontWeight="700">
                      Seleccionar Gancho
                    </Typography>
                    {selectedGancho?.id && (
                      <Chip
                        size="small"
                        icon={<CheckCircle />}
                        label={`#${String(selectedGancho.id).padStart(2, "0")}`}
                        sx={{
                          height: 20,
                          fontSize: "0.65rem",
                          bgcolor: "#e8f5e9",
                          color: "#2e7d32",
                          "& .MuiChip-icon": { color: "#2e7d32", fontSize: 12 },
                        }}
                      />
                    )}
                  </Box>

                  {!form?.savedBike && (
                    <Alert
                      severity="info"
                      icon={<Info />}
                      sx={{ mb: 1.5, borderRadius: "8px", fontSize: "0.75rem", py: 0.5 }}
                    >
                      Guarde el usuario y la bicicleta primero.
                    </Alert>
                  )}

                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "repeat(auto-fill, minmax(80px, 1fr))",
                      gap: 0.75,
                      p: 1.5,
                      bgcolor: "#fafafa",
                      borderRadius: 1,
                      border: "1px solid",
                      borderColor: "divider",
                      maxHeight: 280,
                      overflowY: "auto",
                      pointerEvents: form?.savedBike ? "auto" : "none",
                    }}
                  >
                    {(ganchos || [])
                      .slice()
                      .sort((a, b) => Number(b.disponible) - Number(a.disponible) || a.id - b.id)
                      .map((gancho) => {
                        const isSelected = selectedGancho?.id === gancho.id;
                        const isAvailable = gancho.disponible;
                        const isEnabled = isIngreso ? isAvailable : !isAvailable;

                        return (
                          <Tooltip
                            key={gancho.id}
                            title={
                              isAvailable ? "Disponible" : `${gancho.usuario?.nombre || "Ocupado"}`
                            }
                          >
                            <Box
                              role="button"
                              aria-label={`Seleccionar gancho ${gancho.id}`}
                              aria-pressed={isSelected}
                              aria-disabled={!isEnabled || !form?.savedBike}
                              tabIndex={isEnabled && form?.savedBike ? 0 : -1}
                              onKeyDown={(event) => {
                                if (event.key === "Enter" || event.key === " ") {
                                  event.preventDefault();
                                  event.currentTarget.click();
                                }
                              }}
                              onClick={() => {
                                if (isEnabled && form?.savedBike) {
                                  setSelectedGancho(gancho);
                                  if (form?.handleChange) form.handleChange("ganchoId", gancho.id);
                                  if (onGanchoSelect) onGanchoSelect(gancho.id);
                                }
                              }}
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                justifyContent: "center",
                                p: 1,
                                borderRadius: 1.5,
                                cursor: isEnabled && form?.savedBike ? "pointer" : "not-allowed",
                                opacity: isEnabled ? 1 : 0.4,
                                bgcolor: isSelected
                                  ? isIngreso
                                    ? "#c8e6c9"
                                    : "#ffcdd2"
                                  : isAvailable
                                  ? "#e8f5e9"
                                  : "#fff4e3",
                                border: `2px solid ${
                                  isSelected
                                    ? isIngreso
                                      ? "#2e7d32"
                                      : "#c62828"
                                    : isAvailable
                                    ? "#a5d6a7"
                                    : "#e9c58f"
                                }`,
                                transition: "all 0.15s ease",
                                "&:hover":
                                  isEnabled && form?.savedBike
                                    ? { transform: "scale(1.08)", boxShadow: 1 }
                                    : {},
                              }}
                            >
                              <DirectionsBike
                                sx={{
                                  fontSize: 18,
                                  color: isAvailable ? "#2e7d32" : "#8b5417",
                                }}
                              />
                              <Typography
                                variant="caption"
                                sx={{
                                  fontWeight: "bold",
                                  fontSize: "0.85rem",
                                  color: isAvailable ? "#2e7d32" : "#8b5417",
                                  mt: 0.25,
                                  lineHeight: 1,
                                }}
                              >
                                #{String(gancho.id).padStart(2, "0")}
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{ fontSize: "0.65rem", color: "#475569", mt: 0.5 }}
                              >
                                {isAvailable ? "Disponible" : "Ocupado"}
                              </Typography>
                            </Box>
                          </Tooltip>
                        );
                      })}
                  </Box>

                  {form?.touched?.ganchoId && !selectedGancho && (
                    <Typography variant="caption" color="error" sx={{ mt: 1, display: "block" }}>
                      Debe seleccionar un gancho
                    </Typography>
                  )}
                </Paper>
              </>
            )}
            {activeStep === 3 && (
              <>
                <IngresoReview formData={formData} gancho={selectedGancho} />
                <Paper
                  elevation={0}
                  sx={{
                    p: 2.5,
                    borderRadius: "12px",
                    border: "1px solid",
                    borderColor: "divider",
                    bgcolor: "#fff",
                  }}
                >
                  <Box display="flex" alignItems="center" gap={1} mb={1.5} flexWrap="wrap">
                    <Box
                      sx={{
                        bgcolor: alpha("#5c6bc0", 0.1),
                        borderRadius: "8px",
                        p: 0.75,
                        display: "flex",
                      }}
                    >
                      <PhotoCamera sx={{ fontSize: 18, color: "#5c6bc0" }} />
                    </Box>
                    <Typography variant="subtitle2" fontWeight="700">
                      Fotos (Opcional)
                    </Typography>
                    <Chip
                      size="small"
                      label={`${totalFotosTomadas}/${totalFotos}`}
                      sx={{
                        height: 20,
                        fontSize: "0.65rem",
                        bgcolor:
                          totalFotosTomadas === totalFotos
                            ? alpha("#4caf50", 0.15)
                            : alpha("#ff9800", 0.15),
                        color: totalFotosTomadas === totalFotos ? "#2e7d32" : "#e65100",
                      }}
                    />
                  </Box>

                  <Grid container spacing={1.5}>
                    {Object.keys(fotos).map((tipo) => (
                      <Grid item xs={12} sm={4} key={tipo}>
                        <Paper
                          sx={{
                            p: 1.5,
                            textAlign: "center",
                            borderRadius: 2,
                            border: fotos[tipo].tomada ? "2px solid #4caf50" : "2px dashed #e0e0e0",
                            bgcolor: fotos[tipo].tomada ? alpha("#4caf50", 0.05) : "#fafafa",
                            cursor: "pointer",
                            transition: "all 0.15s",
                            "&:hover": { transform: "translateY(-2px)", boxShadow: 1 },
                          }}
                          onClick={() => openCamera(tipo)}
                        >
                          {fotos[tipo].tomada ? (
                            <Box>
                              <Avatar
                                src={fotos[tipo].preview}
                                variant="rounded"
                                sx={{
                                  width: "100%",
                                  height: 80,
                                  borderRadius: 1,
                                  mb: 0.5,
                                  objectFit: "cover",
                                }}
                              />
                              <Box
                                display="flex"
                                alignItems="center"
                                justifyContent="center"
                                gap={0.5}
                              >
                                <CheckCircle sx={{ fontSize: 12, color: "#4caf50" }} />
                                <Typography
                                  variant="caption"
                                  sx={{ color: "#2e7d32", fontWeight: 600, fontSize: "0.7rem" }}
                                >
                                  {getFotoLabel(tipo)}
                                </Typography>
                              </Box>
                            </Box>
                          ) : (
                            <Box>
                              <Box
                                sx={{
                                  width: "100%",
                                  height: 80,
                                  borderRadius: 1,
                                  bgcolor: "#f5f5f5",
                                  display: "flex",
                                  flexDirection: "column",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  mb: 0.5,
                                }}
                              >
                                {getFotoIcon(tipo)}
                                <Typography variant="caption" sx={{ color: "#9e9e9e", mt: 0.25 }}>
                                  {getFotoLabel(tipo)}
                                </Typography>
                              </Box>
                              <Typography
                                variant="caption"
                                sx={{ color: "#9e9e9e", fontSize: "0.65rem" }}
                              >
                                Tap para tomar
                              </Typography>
                            </Box>
                          )}
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>
                </Paper>
              </>
            )}
          </Stack>
        </DialogContent>

        <DialogActions
          sx={{
            p: { xs: 1.5, sm: 2.5 },
            borderTop: "1px solid #dce5ed",
            bgcolor: "#fff",
            flexDirection: "column",
            alignItems: "stretch",
            gap: 1,
          }}
        >
          {activeStep === 3 && (
            <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end", flexWrap: "wrap" }}>
              <Button
                size="small"
                startIcon={<Print />}
                disabled={busy}
                onClick={openPreview}
                sx={{ color: "#526579", textTransform: "none" }}
              >
                Vista previa
              </Button>
              <Button
                size="small"
                startIcon={<Print />}
                disabled={busy || !registroCompletado}
                onClick={handleImprimirTicket}
                sx={{ color: "#526579", textTransform: "none" }}
              >
                {registroCompletado ? "Reimprimir" : "Imprimir"}
              </Button>
            </Box>
          )}
          <Box sx={{ display: "flex", gap: 1, justifyContent: "space-between", flexWrap: "wrap" }}>
            <Button
              onClick={onClose}
              disabled={busy}
              sx={{ color: "#526579", textTransform: "none" }}
            >
              {registroCompletado ? "Cerrar" : "Cancelar"}
            </Button>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
              {activeStep > 0 && !registroCompletado && (
                <Button
                  onClick={() => setActiveStep(activeStep - 1)}
                  disabled={busy}
                  sx={{ color: "#245a83", textTransform: "none" }}
                >
                  Atrás
                </Button>
              )}
              {!registroCompletado && (
                <Button
                  variant="contained"
                  disabled={nextDisabled}
                  onClick={continueStep}
                  startIcon={busy ? <CircularProgress size={16} color="inherit" /> : undefined}
                  sx={{
                    bgcolor: "#193e54",
                    color: "#fff",
                    fontWeight: 600,
                    textTransform: "none",
                    "&:hover": { bgcolor: "#123046" },
                    "&.Mui-disabled": { bgcolor: "#e3e9ef", color: "#65758a" },
                  }}
                >
                  {nextLabel}
                </Button>
              )}
            </Box>
          </Box>
        </DialogActions>
      </Dialog>
    </>
  );
};

BicicletaModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  modo: PropTypes.oneOf(["INGRESO", "SALIDA"]),
  ganchos: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      disponible: PropTypes.bool.isRequired,
      usuario: PropTypes.object,
      cicloId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    })
  ),
  ganchoSeleccionado: PropTypes.object,
  onGanchoSelect: PropTypes.func,
  formData: PropTypes.object,
  onFormDataChange: PropTypes.func,
  onSaveUser: PropTypes.func,
  onSaveBike: PropTypes.func,
  onSubmit: PropTypes.func,
  users: PropTypes.array,
  loading: PropTypes.bool,
};

BicicletaModal.defaultProps = {
  modo: "INGRESO",
  ganchos: [],
  ganchoSeleccionado: null,
  onGanchoSelect: null,
  formData: {},
  onFormDataChange: null,
  onSaveUser: null,
  onSaveBike: null,
  onSubmit: null,
  users: [],
  loading: false,
};

export default BicicletaModal;
